import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/apiService';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

const ComLogList = () => {
  const [comLogs, setComLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const limit = 25;

  // Filter-State inklusive Local Storage
  const [filters, setFilters] = useState({
    customerNumber: null,
    customerName: null,
    documentType: null,
    documentNumber: null,
    emailRecipient: null,
    emailSubject: null,
    sentDate: null,
  });

  // Live-Suche für Betreff (CreatableSelect, ohne Enter)
const [subjectInput, setSubjectInput] = useState('');
const [debouncedSubjectInput, setDebouncedSubjectInput] = useState('');

useEffect(() => {
  const t = setTimeout(() => setDebouncedSubjectInput(subjectInput), 500);
  return () => clearTimeout(t);
}, [subjectInput]);

useEffect(() => {
  setFilters(prev => ({
    ...prev,
    emailSubject: debouncedSubjectInput
      ? { value: debouncedSubjectInput, label: debouncedSubjectInput }
      : null
  }));
  setPage(1);
}, [debouncedSubjectInput]);

  useEffect(() => {
    localStorage.setItem('comLogListFilters', JSON.stringify(filters));
    setPage(1); // Seite zurücksetzen bei Filteränderungen
  }, [filters]);
  

  // Neuen State für serverseitig ermittelte Filteroptionen
  const [serverFilterOptions, setServerFilterOptions] = useState({
    documentTypes: [],
    documentNumbers: [],
    emailRecipients: [],
    emailSubjects: [],
    sentDates: [],
  });

  // Abruf des neuen Endpunkts für Filteroptionen
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await api.get('/comlogs/filter-options');
        setServerFilterOptions(response.data);
      } catch (err) {
        console.error('Fehler beim Abrufen der Filteroptionen:', err);
      }
    };
    fetchFilterOptions();
  }, []);

  // Funktion zum Erzeugen des Query-Strings basierend auf Filter und Pagination
  const buildQuery = () => {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    if (filters.customerNumber) {
      params.append('customerNumber', filters.customerNumber.value);
    }
    if (filters.customerName) {
      params.append('customerName', filters.customerName.value);
    }
    if (filters.documentType) {
      params.append('documentType', filters.documentType.value);
    }
    if (filters.documentNumber) {
      params.append('documentNumber', filters.documentNumber.value);
    }
    if (filters.emailRecipient) {
      params.append('emailRecipient', filters.emailRecipient.value);
    }
    if (filters.emailSubject) {
      params.append('emailSubject', filters.emailSubject.value);
    }
    if (filters.sentDate) {
      params.append('sentDate', filters.sentDate.value);
    }
    return params.toString() ? `?${params.toString()}` : '';
  };

  // API-Request: Filter-Parameter an den Server übergeben
  useEffect(() => {
    const fetchComLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const query = buildQuery();
        console.log('Query:', query);
        const response = await api.get(`/comlogs${query}`);
        setComLogs(response.data.data || response.data);
        // Setze totalPages aus der Antwort (wenn vorhanden)
        if (response.data.pages) {
          setTotalPages(response.data.pages);
        }
        setLoading(false);
      } catch (err) {
        setError('Fehler beim Abrufen der ComLogs');
        setLoading(false);
      }
    };

    fetchComLogs();
  }, [page, filters]);

  // Ableiten von Filteroptionen aus den aktuell geladenen ComLogs (für Kundennummer und -name)
  const optionsCustomerNumber = useMemo(() => {
    const values = Array.from(new Set(
      comLogs.map(log => log.customer && log.customer.customerNumber).filter(Boolean)
    ));
    return values.map(val => ({ value: val, label: val }));
  }, [comLogs]);

  const optionsCustomerName = useMemo(() => {
    const values = Array.from(new Set(
      comLogs.map(log => log.customer && log.customer.company).filter(Boolean)
    ));
    return values.map(val => ({ value: val, label: val }));
  }, [comLogs]);

  // Verwende die vom Server gelieferten Optionen für die restlichen Filter
  const optionsDocumentType = useMemo(() => {
    return serverFilterOptions.documentTypes.map(val => ({ value: val, label: val }));
  }, [serverFilterOptions.documentTypes]);

  const optionsDocumentNumber = useMemo(() => {
    return serverFilterOptions.documentNumbers.map(val => ({ value: val, label: val }));
  }, [serverFilterOptions.documentNumbers]);

  const optionsEmailRecipient = useMemo(() => {
    return serverFilterOptions.emailRecipients.map(val => ({ value: val, label: val }));
  }, [serverFilterOptions.emailRecipients]);

  const optionsEmailSubject = useMemo(() => {
    return serverFilterOptions.emailSubjects.map(val => ({ value: val, label: val }));
  }, [serverFilterOptions.emailSubjects]);

  const optionsSentDate = useMemo(() => {
    return serverFilterOptions.sentDates.map(val => ({ value: val, label: val }));
  }, [serverFilterOptions.sentDates]);

  if (loading) {
    return <div>Lade ComLogs...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (comLogs.length === 0) {
    return <div>Keine ComLog-Einträge gefunden.</div>;
  }

const resetFilters = () => {
  setFilters({
    customerNumber: null,
    customerName: null,
    documentType: null,
    documentNumber: null,
    emailRecipient: null,
    emailSubject: null,
    sentDate: null,
  });
  setSubjectInput(''); // auch das Tippfeld leeren
  setPage(1);
  try { localStorage.removeItem('comLogListFilters'); } catch {}
};

  return (
    <div>
      <h2>ComLog Liste</h2>
      <Helmet>
        <title>ComLog Liste</title>
        <meta name="description" content="Liste der ComLog-Einträge" />
      </Helmet>

      {/* Filterzeile oberhalb der Tabelle */}
      <div style={{ marginBottom: '20px' }}>
        <h3></h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '10px' }}>
          <Select
            options={optionsCustomerNumber}
            placeholder="Kundennummer"
            isClearable
            value={filters.customerNumber}
            onChange={(selected) => setFilters({ ...filters, customerNumber: selected })}
          />
          <Select
            options={optionsCustomerName}
            placeholder="Kundenname"
            isClearable
            value={filters.customerName}
            onChange={(selected) => setFilters({ ...filters, customerName: selected })}
          />
          <Select
            options={optionsDocumentType}
            placeholder="Dokumenttyp"
            isClearable
            value={filters.documentType}
            onChange={(selected) => setFilters({ ...filters, documentType: selected })}
          />
          <Select
            options={optionsDocumentNumber}
            placeholder="Dokumentnummer"
            isClearable
            value={filters.documentNumber}
            onChange={(selected) => setFilters({ ...filters, documentNumber: selected })}
          />
          <Select
            options={optionsEmailRecipient}
            placeholder="Email Empfänger"
            isClearable
            value={filters.emailRecipient}
            onChange={(selected) => setFilters({ ...filters, emailRecipient: selected })}
          />
          <CreatableSelect
  options={optionsEmailSubject}
  placeholder="Betreff"
  isClearable
  value={filters.emailSubject}
  // Auswahl/Clear via Dropdown bleibt möglich
  onChange={(selected) => setFilters({ ...filters, emailSubject: selected })}
  // Live-Suche beim Tippen (debounced via subjectInput)
  onInputChange={(inputValue, actionMeta) => {
    if (actionMeta.action === 'input-change') setSubjectInput(inputValue || '');
    return inputValue;
  }}
  // Enter erzeugt weiterhin einen freien Wert
  onCreateOption={(inputValue) =>
    setFilters({ ...filters, emailSubject: { value: inputValue, label: inputValue } })
  }
/>

          <Select
            options={optionsSentDate}
            placeholder="Versanddatum"
            isClearable
            value={filters.sentDate}
            onChange={(selected) => setFilters({ ...filters, sentDate: selected })}
          />
          
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Kundennummer</th>
            <th>Kundenname</th>
            <th>Dokumenttyp</th>
            <th>Dokumentnummer</th>
            <th>Email Empfänger</th>
            <th>Betreff</th>
            <th>Versender</th>
            <th>Versanddatum</th>
            <th>Link</th>
          </tr>
        </thead>
        <tbody>
          {comLogs.map((log) => (
            <tr key={log.id}>
              <td>{log.id}</td>
              <td>{log.customer && log.customer.customerNumber ? log.customer.customerNumber : '-'}</td>
              <td>{log.customer && log.customer.company ? log.customer.company : '-'}</td>
              <td>{log.documentType}</td>
              <td>{log.documentNumber || '-'}</td>
              <td>{log.emailRecipient}</td>
              <td>{log.emailSubject || '-'}</td>
              <td>
                {log.updater
                  ? `${log.updater.firstName} ${log.updater.lastName}`
                  : '-'}
              </td>
              <td>{new Date(log.sentDate).toLocaleDateString()}</td>
              <td>
                {log.documentLink ? (
                  <Link to={log.documentLink} style={{ textDecoration: 'underline', color: 'blue' }}>
                    Formular
                  </Link>
                ) : (
                  '-'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: '1rem' }}>
        <button onClick={() => setPage(page > 1 ? page - 1 : 1)} disabled={page === 1}
          style={{
            fontSize: '15px',
            padding: '8px 12px',
            backgroundColor:'rgb(155, 155, 155)',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }} >
          Vorherige
        </button>
        <span style={{ margin: '0 1rem' }}>Seite {page}</span>
        <button onClick={() => setPage(page + 1)} disabled={page >= totalPages}
          style={{
            fontSize: '15px',
            padding: '8px 12px',
            backgroundColor:'rgb(155, 155, 155)',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }} >
  Nächste
</button>
      </div>
      <br />
    </div>
  );
};

export default ComLogList;
